import { Fragment, type ReactNode } from 'react'

import { cn, useDocumentTitle } from '@/shared/lib'
import { AnimatedIcon } from '@/shared/ui/animated-icon'
import { IconButton } from '@/shared/ui/icon-button'
import { ArrowLeftIcon } from '@/shared/ui/icons/arrow-left'

type RecordHeaderProps = {
  onBack: () => void
  /** Accessible name for the back control — «К кампаниям». */
  backLabel: string
  title: ReactNode
  /** Mono when the title is an identifier rather than a name — an email, an id. */
  isMonoTitle?: boolean
  /** Statuses beside the title. Never an icon on its own — StatusBadge. */
  badges?: ReactNode
  /** Two or three facts, joined by middots. Anything longer belongs in the body. */
  meta?: ReactNode[]
  actions?: ReactNode
  className?: string
}

/**
 * The header of the record archetype: back, name, status, two or three
 * metadata, actions — one line, and it is a heading rather than the page's
 * first card.
 *
 * It exists so the title size lives in one place. Two detail pages had each
 * inlined their own `clamp()`, and they had drifted apart by two pixels.
 */
export function RecordHeader({
  onBack,
  backLabel,
  title,
  isMonoTitle = false,
  badges,
  meta,
  actions,
  className,
}: RecordHeaderProps) {
  // The record's own name in the tab — a campaign or a user, not «Admin».
  useDocumentTitle(typeof title === 'string' ? title : undefined)

  return (
    <header
      data-slot="record-header"
      className={cn('flex flex-wrap items-start gap-4', className)}
    >
      <IconButton label={backLabel} size="sm" className="mt-1" onClick={onBack}>
        <AnimatedIcon icon={ArrowLeftIcon} />
      </IconButton>

      <div className="min-w-65 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1
            className={cn(
              'text-[clamp(22px,2.4vw,24px)] leading-heading font-semibold tracking-h1',
              isMonoTitle && 'font-mono',
            )}
          >
            {title}
          </h1>
          {badges}
        </div>

        {meta !== undefined && meta.length > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-muted-foreground">
            {meta.map((item, index) => (
              <Fragment key={index}>
                {index > 0 && <span aria-hidden>·</span>}
                {item}
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {actions !== undefined && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  )
}
